import React, { useEffect, useState } from "react";
import { motion, Variants } from "framer-motion";
import { XCircle, Star } from "lucide-react";
import { paymentFailed } from "../api/api"; // <-- add karmaPaymentFailed if needed
import { useAuth } from "../context/AuthContext";

const confettiCount = 30;

const Confetti: React.FC = () => {
    const pieces = Array.from({ length: confettiCount });
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {pieces.map((_, i) => {
                const size = Math.random() * 8 + 4;
                const left = Math.random() * 100;
                const delay = Math.random() * 0.5;
                const duration = Math.random() * 2 + 2;
                // Red/amber tone confetti
                const hue = 4 + Math.random() * 26;
                return (
                    <motion.div
                        key={i}
                        className="rounded-sm"
                        style={{
                            width: size,
                            height: size,
                            backgroundColor: `hsl(${hue}, 98%, 62%)`,
                            left: `${left}%`,
                            top: "-10%",
                            position: "absolute",
                        }}
                        animate={{
                            y: ["-10%", "110%"],
                            rotate: [0, 360],
                        }}
                        transition={{
                            delay,
                            duration,
                            ease: "easeOut",
                            repeat: Infinity,
                            repeatType: "loop",
                        }}
                    />
                );
            })}
        </div>
    );
};

const textVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { delay: 0.45 + i * 0.18, duration: 0.7, ease: "easeOut" },
    }),
};

const Failure: React.FC = () => {
    const { isLoading, isAuthenticated } = useAuth();
    const [isKarma, setIsKarma] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topic = params.get("topic");
        if (topic === "karma") {
            setIsKarma(true);
            const session_id = params.get("session_id");
            if (session_id && isAuthenticated) {
                // If you have a special API endpoint for karma, call it here
                // Otherwise use paymentFailed for both
                // await karmaPaymentFailed(session_id);
                paymentFailed(session_id); // Call your backend to mark as failed
            }
            return;
        }

        // Regular booking payment fail
        const payment_session_id = params.get("session_id");
        if (!payment_session_id) return;
        if (isLoading) return;
        if (isAuthenticated) {
            paymentFailed(payment_session_id);
        }
    }, [isLoading, isAuthenticated]);

    return (
        <div className="relative flex flex-col items-center justify-center w-screen h-screen bg-gradient-to-br from-white via-red-50 to-red-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            <Confetti />
            <div className="relative flex flex-col items-center justify-center rounded-2xl bg-white/95 dark:bg-gray-900/95 border border-red-400/30 shadow-2xl px-8 py-12 max-w-lg w-full z-10">
                <motion.div
                    className="mb-5"
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.65, type: "spring", bounce: 0.35 }}
                >
                    {isKarma ? (
                        <Star className="w-24 h-24 text-yellow-400 drop-shadow animate-bounce" strokeWidth={1.5} />
                    ) : (
                        <XCircle className="w-24 h-24 text-red-500 drop-shadow" strokeWidth={1.5} />
                    )}
                </motion.div>

                <motion.h1
                    className={`text-3xl md:text-4xl font-extrabold mb-2 tracking-tight ${isKarma
                        ? "text-yellow-800 dark:text-yellow-100"
                        : "text-gray-800 dark:text-gray-100"
                    }`}
                    custom={0}
                    initial="hidden"
                    animate="visible"
                    variants={textVariants}
                >
                    {isKarma
                        ? "Karma Upgrade Failed"
                        : "Oops! Something Went Wrong"}
                </motion.h1>

                <motion.p
                    className="text-gray-700 mb-5 text-center max-w-md font-medium dark:text-gray-200"
                    custom={1}
                    initial="hidden"
                    animate="visible"
                    variants={textVariants}
                >
                    {isKarma ? (
                        <>
                            Your <b>Karma Score upgrade</b> could not be completed. <br />
                            <span className="block mt-2 text-yellow-700 dark:text-yellow-300 font-semibold">
                                No worries – your card has not been charged. Try again or contact support.
                            </span>
                        </>
                    ) : (
                        <>
                            We couldn’t complete your booking. This could be due to a temporary issue with payment, your network, or restaurant availability.
                            <br />
                            <span className="block mt-2 text-red-700 dark:text-red-300 font-semibold">
                                Don’t worry – your card has not been charged.
                            </span>
                        </>
                    )}
                </motion.p>

                <motion.div
                    className="mb-8 text-gray-600 text-center max-w-lg text-sm leading-relaxed dark:text-gray-300"
                    custom={2}
                    initial="hidden"
                    animate="visible"
                    variants={textVariants}
                >
                    {isKarma ? (
                        <>
                            <span className="block mb-1">
                                You can try upgrading again in a few seconds.
                            </span>
                            <span className="block mb-1">
                                If you keep seeing this error,{" "}
                                <a href="/contact" className="underline text-yellow-600 font-semibold hover:text-yellow-700">
                                    contact our support
                                </a>
                                . We’ll help you boost your Karma!
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="block mb-1">
                                You can try booking again in a few seconds, or choose another time slot.
                            </span>
                            <span className="block mb-1">
                                If you keep seeing this error,{" "}
                                <a href="/contact" className="underline text-red-600 font-semibold hover:text-red-700">
                                    contact our support
                                </a>
                                . Our team is here to help!
                            </span>
                            <span className="block mt-2">
                                <b>Tip:</b> Double-check your card or payment method and internet connection before retrying.
                            </span>
                        </>
                    )}
                </motion.div>

                <div className="flex gap-4 flex-wrap">
                    <motion.button
                        className="px-8 py-3 bg-gray-200 text-gray-700 font-bold rounded-md shadow-lg hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-none text-lg"
                        custom={4}
                        initial="hidden"
                        animate="visible"
                        variants={textVariants}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => (window.location.href = "/")}
                    >
                        Back to Home
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

export default Failure;
